import { useState, useMemo, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import i18n from "@i18n/i18n";
import _ from "lodash";
import { Model } from "@api/apiClient";

export interface CreateVersionDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateVersion: (suffix: string) => Promise<void>;
  loading?: boolean;
  currentModel?: Model | null;
  projectModels?: Model[];
}

const CreateVersionDialog = (props: CreateVersionDialogProps) => {
  const { open, onClose, onCreateVersion, loading = false, currentModel, projectModels = [] } = props;
  const [suffix, setSuffix] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Get model name without extension and extract extension
  const { modelBaseName, fileExtension } = useMemo(() => {
    if (!currentModel?.apsItemName) return { modelBaseName: "Model", fileExtension: "" };

    const lastDotIndex = currentModel.apsItemName.lastIndexOf('.');
    if (lastDotIndex > 0) {
      const baseName = currentModel.apsItemName.substring(0, lastDotIndex);
      const extension = currentModel.apsItemName.substring(lastDotIndex); // includes the dot
      return { modelBaseName: baseName, fileExtension: extension };
    }

    return { modelBaseName: currentModel.apsItemName, fileExtension: "" };
  }, [currentModel?.apsItemName]);

  // Calculate the next version number
  const nextVersion = useMemo(() => {
    if (!currentModel?.apsItemName || !currentModel?.projectId) return 1;

    // Find all models with the same apsItemName in the current project
    const sameItemModels = projectModels.filter(
      (model) =>
        model.apsItemName === currentModel.apsItemName &&
        model.projectId === currentModel.projectId
    );

    // Get the maximum version number and add 1
    const maxVersion = sameItemModels.reduce((max, model) => {
      const modelVersion = model.version || 0;
      return Math.max(max, modelVersion);
    }, 0);

    return maxVersion + 1;
  }, [currentModel?.apsItemName, currentModel?.projectId, projectModels]);



  const handleSuffixChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSuffix(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitted(true);

    // Validate suffix is not empty
    if (!suffix.trim()) {
      setIsSubmitted(false);
      return;
    }

    try {
      await onCreateVersion(suffix.trim());
      handleClose();
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleClose = () => {
    setSuffix("");
    setIsSubmitted(false);
    onClose();
  };

  // Reset suffix when dialog opens
  useEffect(() => {
    if (open) {
      // Always start with empty suffix in create mode
      setSuffix("");
      setIsSubmitted(false);
    }
  }, [open]);

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"sm"}
      open={open}
      onClose={handleClose}
      aria-labelledby="create-version-dialog-title"
    >
      <DialogTitle
        id="create-version-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {i18n.t("scope-tab.create-version-dialog.title")}
        <IconButton title="Close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Stack mt={1} spacing={2}>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: '4px',
                overflow: 'hidden',
                height: '56px',
                '&:focus-within': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                }
              }}
            >
              {/* Non-editable prefix */}
              <Box
                sx={{
                  backgroundColor: 'grey.50',
                  padding: '16px 14px',
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  borderRight: '1px solid',
                  borderColor: 'grey.200',
                  whiteSpace: 'nowrap',
                }}
              >
                {modelBaseName}-
              </Box>

              {/* Editable suffix */}
              <TextField
                value={suffix}
                onChange={handleSuffixChange}
                variant="outlined"
                autoFocus
                placeholder="suffix"
                required
                error={isSubmitted && !suffix.trim()}
                helperText={isSubmitted && !suffix.trim() ? "Suffix is required" : ""}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    height: '56px',
                    border: 'none',
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover fieldset': {
                      border: 'none',
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '16px 14px',
                  }
                }}
              />

              <Box
                sx={{
                  backgroundColor: 'grey.50',
                  padding: '16px 14px',
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  borderLeft: '1px solid',
                  borderColor: 'grey.200',
                  whiteSpace: 'nowrap',
                }}
              >
                -ICT{currentModel?.apsVersion || 0}.{nextVersion}{fileExtension}
              </Box>
            </Box>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {i18n.t("scope-tab.create-version-dialog.cancel-button")}
        </Button>
        <Button
          loading={!!(isSubmitted || loading)}
          variant="contained"
          color="primary"
          onClick={(e) => handleSubmit(e as any)}
          disabled={!currentModel?.apsItemName || !currentModel.apsItemName?.trim() || !suffix.trim()}
        >
          {i18n.t("scope-tab.create-version-dialog.create-button")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateVersionDialog; 